//@flow
import FunctionStore from '../Base/functionStoreClass';
import { apiUpdateState, applyServerDefaultData, updateState } from '../Base/storeUtils';
import Constants from '../../constants';
import Api from '../../APIInterfaces/products';
import ProductsCollection from './productsCollection';
import { ProductRecord } from './productModel';
import _ from 'underscore';
import testData from '../../Utils/testData';
import {deriveProductReviewSummary} from '../../Utils/review-utils';

import type { HandlerAdd, StoreState } from '../Base/functionStoreClass';
import type ProductModel, {ProductObject} from './productModel';
import type { ReviewSummaryObject, ReviewSummaryData } from './reviewSummaryModel';
import typeof QuoteStore from '../Quote';

type Stores = {
  QuoteStore:QuoteStore,
};

// Adding this for convenience so that
// avrio can be run with some static test data
// without requiring mage api to be running
// eslint-disable-next-line no-undef
const USE_TEST_DATA = process.env.USE_TEST_DATA === 'true';

export default class ProductsStoreClass extends FunctionStore {
  stores:Stores

  getProducts():ProductsCollection {
    return this._resolvePropertyObject('products', ProductsCollection, Api.getProducts);
  }

  getReviewSummaryData():ReviewSummaryData {
    const data = this.getState().get('reviewSummaries');
    return data || {};
  }

  // TODO: memoize this
  getProductReviewSummary(product:ProductModel):ReviewSummaryObject|null {
    const data = this.getReviewSummaryData();
    return deriveProductReviewSummary(product, data);
  }

  _initialState(state:StoreState) {
    applyServerDefaultData('productsData', state, _setProducts);
    applyServerDefaultData('reviewsData', state, _setReviewSummaries);
    if (USE_TEST_DATA) {
      _setProducts(state, testData.productsData);
    }
  }

  _setup(onSuccess:HandlerAdd, onError:HandlerAdd) {
    onSuccess(Constants.GET_PRODUCTS_SUCCESS, apiUpdateState(_setProducts));
    onSuccess(Constants.GET_PRODUCTS_BY_SKUS_SUCCESS, apiUpdateState(_setNewProducts));
    onSuccess(Constants.GET_PRODUCT_REVIEW_SUMMARIES_SUCCESS, updateState(_setReviewSummaries));
    onSuccess(Constants.GET_QUOTES_SUCCESS, _getQuoteProducts, [this.stores.QuoteStore]);
    onSuccess(Constants.GET_QUOTE_SUCCESS, _getQuoteProducts, [this.stores.QuoteStore]);
  }
}

function _setProducts(data:StoreState, resp:{[string]:ProductObject}) {
  const records = _.map(resp, (product) => new ProductRecord((product:any)));
  data.set('products', records);
}

function _setNewProducts(data:StoreState, resp:{[string]:ProductObject}) {
  const newProds = _.map(resp, (product) => new ProductRecord((product:any)));
  const currentProds = data.get('products');
  const products = currentProds ? currentProds.concat(newProds) : newProds;
  data.set('products', products);
}

function _setReviewSummaries(data:StoreState, productReviewSummaries:ReviewSummaryObject[]) {
  if (!productReviewSummaries) return;
  const asObject = {};
  productReviewSummaries.forEach((summary:ReviewSummaryObject) => {
    asObject[summary.productSku] = summary;
  });

  data.set('reviewSummaries', asObject);
}

function _getQuoteProducts(store:ProductsStoreClass) {
  // Not visible products won't show up on the /products api response so we need to fetch them
  const QuoteStore = store.stores.QuoteStore;
  const quote = QuoteStore.getQuote();
  const activeSubQuote = QuoteStore.getSubActiveQuote();
  const activeSubQuoteId = activeSubQuote.getId();
  const quoteItems = QuoteStore.getQuoteItems(quote.getId());
  const activeSubQuoteItems = QuoteStore.getQuoteItems(activeSubQuoteId);

  const fetchedSkus = store.getProducts().getSkus();

  // Find skus we need to fetch
  const skus = quoteItems.getSkus()
    .concat(activeSubQuoteItems.getSkus())
    .filter((sku, index, skus) => {
      return skus.indexOf(sku) === index && !fetchedSkus.includes(sku);
    });

  if (skus.length) Api.getProductsBySkus(skus, true);
}
